import { useState, useCallback, useRef, useEffect } from 'react'

interface DragState {
  isDragging: boolean
  dragIndex: number | null
}

interface ResizeState {
  isResizing: boolean
  resizeColumn: string | null
  startX: number
  startWidth: number
}

export const useColumnDragResize = (
  onColumnReorder?: (fromIndex: number, toIndex: number) => void,
  onColumnResize?: (columnKey: string, width: number) => void
) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragIndex: null,
  })
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)

  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    resizeColumn: null,
    startX: 0,
    startWidth: 0,
  })

  const dragPreviewRef = useRef<HTMLDivElement>(null)

  // Use refs for drag tracking to avoid useEffect dependency churn
  const dragRef = useRef<{
    isDragging: boolean
    dragIndex: number | null
    dragOffset: { x: number; y: number }
    headerRects: DOMRect[]
  }>({
    isDragging: false,
    dragIndex: null,
    dragOffset: { x: 0, y: 0 },
    headerRects: [],
  })

  const resizeRef = useRef<{
    isResizing: boolean
    resizeColumn: string | null
    startX: number
    startWidth: number
  }>({
    isResizing: false,
    resizeColumn: null,
    startX: 0,
    startWidth: 0,
  })

  // Stable refs for callbacks to avoid stale closures
  const onColumnReorderRef = useRef(onColumnReorder)
  onColumnReorderRef.current = onColumnReorder

  const onColumnResizeRef = useRef(onColumnResize)
  onColumnResizeRef.current = onColumnResize

  // Ref callback to collect header element refs
  const headerRefsMap = useRef<Map<number, HTMLElement>>(new Map())

  const setHeaderRef = useCallback(
    (index: number) => (el: HTMLTableCellElement | null) => {
      if (el) {
        headerRefsMap.current.set(index, el)
      } else {
        headerRefsMap.current.delete(index)
      }
    },
    []
  )

  // Capture bounding rects of all column headers at drag start
  const captureHeaderRects = useCallback(() => {
    const rects: DOMRect[] = []
    const sortedEntries = Array.from(headerRefsMap.current.entries()).sort(
      ([a], [b]) => a - b
    )
    for (const [, el] of sortedEntries) {
      rects.push(el.getBoundingClientRect())
    }
    return rects
  }, [])

  // Find drop slot based on mouse X position.
  // Returns 0..N where N = rects.length.
  // Value i means "insert before column i" (N = after last column).
  const findDropSlot = useCallback(
    (clientX: number, rects: DOMRect[]): number | null => {
      if (rects.length === 0) return null

      for (let i = 0; i < rects.length; i++) {
        const rect = rects[i]
        const midX = rect.left + rect.width / 2
        if (clientX < midX) {
          return i
        }
      }
      // Past the last column
      return rects.length
    },
    []
  )

  // Handle column drag start
  const handleDragStart = useCallback(
    (index: number, event: React.MouseEvent) => {
      if (!onColumnReorderRef.current) return

      event.preventDefault()
      event.stopPropagation()

      const rect = (event.currentTarget as HTMLElement)
        .closest('th')
        ?.getBoundingClientRect()
      if (!rect) return

      const headerRects = captureHeaderRects()

      dragRef.current = {
        isDragging: true,
        dragIndex: index,
        dragOffset: {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        },
        headerRects,
      }

      setDragState({ isDragging: true, dragIndex: index })

      // Position preview immediately
      if (dragPreviewRef.current) {
        dragPreviewRef.current.style.left = `${rect.left}px`
        dragPreviewRef.current.style.top = `${rect.top}px`
      }

      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'
    },
    [captureHeaderRects]
  )

  // Handle column resize start
  const handleResizeStart = useCallback(
    (columnKey: string, event: React.MouseEvent) => {
      if (!onColumnResizeRef.current) return

      event.preventDefault()
      event.stopPropagation()

      const resizeHandle = event.currentTarget as HTMLElement
      const headerElement = resizeHandle.closest('th') as HTMLElement
      if (!headerElement) return

      const currentWidth = headerElement.getBoundingClientRect().width

      resizeRef.current = {
        isResizing: true,
        resizeColumn: columnKey,
        startX: event.clientX,
        startWidth: currentWidth,
      }

      setResizeState({
        isResizing: true,
        resizeColumn: columnKey,
        startX: event.clientX,
        startWidth: currentWidth,
      })

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    []
  )

  // Single global listener effect - deps are stable
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      // Handle drag preview movement
      if (dragRef.current.isDragging && dragPreviewRef.current) {
        dragPreviewRef.current.style.left = `${event.clientX - dragRef.current.dragOffset.x}px`
        dragPreviewRef.current.style.top = `${event.clientY - dragRef.current.dragOffset.y}px`

        // Update drop target slot
        const slot = findDropSlot(event.clientX, dragRef.current.headerRects)
        setDropTargetIndex(slot)
      }

      // Handle resize
      if (resizeRef.current.isResizing && resizeRef.current.resizeColumn) {
        const deltaX = event.clientX - resizeRef.current.startX
        const newWidth = Math.max(50, resizeRef.current.startWidth + deltaX)
        onColumnResizeRef.current?.(resizeRef.current.resizeColumn, newWidth)
      }
    }

    const handleMouseUp = (event: MouseEvent) => {
      const wasDragging = dragRef.current.isDragging
      const wasResizing = resizeRef.current.isResizing

      // Handle drag end
      if (wasDragging && dragRef.current.dragIndex !== null) {
        const dropSlot = findDropSlot(
          event.clientX,
          dragRef.current.headerRects
        )
        const dragIdx = dragRef.current.dragIndex

        // Dropping at slot === dragIdx or dragIdx+1 is a no-op
        // (inserting before yourself or before the column right after you)
        if (
          dropSlot !== null &&
          dropSlot !== dragIdx &&
          dropSlot !== dragIdx + 1
        ) {
          onColumnReorderRef.current?.(dragIdx, dropSlot)
        }

        dragRef.current = {
          isDragging: false,
          dragIndex: null,
          dragOffset: { x: 0, y: 0 },
          headerRects: [],
        }

        setDragState({ isDragging: false, dragIndex: null })
        setDropTargetIndex(null)
      }

      // Handle resize end
      if (wasResizing) {
        resizeRef.current = {
          isResizing: false,
          resizeColumn: null,
          startX: 0,
          startWidth: 0,
        }

        setResizeState({
          isResizing: false,
          resizeColumn: null,
          startX: 0,
          startWidth: 0,
        })
      }

      // Only reset cursor if we were actively dragging/resizing
      if (wasDragging || wasResizing) {
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [findDropSlot])

  return {
    dragState,
    dropTargetIndex,
    resizeState,
    dragPreviewRef,
    handleDragStart,
    handleResizeStart,
    setHeaderRef,
  }
}
